import { useCallback, useContext, useMemo, useRef } from 'react';

import { useConfluencePageAri } from '@atlassian/ai-summary';
import { useConvoAIStreaming, ConvoAIResponseState } from '@atlassian/ai-agent-streaming';
import type {
	UserConvoAIStreamingConfig,
	ConvoAIStreamingState,
} from '@atlassian/ai-agent-streaming';
import type {
	AiFeatureRequestEnvelopeEditorAiFeatureRequest,
	EditorAiAnswerPartResponseMessage,
	EditorAiErrorResponseMessage,
	EditorAiFinalResponseMessage,
} from '@atlassian/convo-ai-api';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';

import { AI_TITLE_GENERATION } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';

interface UseTitleGenerationStreamingProps {
	contentId: string;
	cloudId: string;
	documentContext: string;
	locale: string;
	skip: boolean;
	onTitleChange?: (title: string) => void;
}

const buildTitleGenerationRequest = ({
	contentAri,
	documentContext,
	locale,
	skip,
}: {
	contentAri: string;
	documentContext: string;
	locale: string;
	skip: boolean;
}): [AiFeatureRequestEnvelopeEditorAiFeatureRequest, UserConvoAIStreamingConfig] => {
	return [
		{
			ai_feature_input: {
				feature: 'suggest_title',
				feature_args: {
					user_input: '',
					document_context: documentContext,
					supportedMarkdownNodes: ['PARAGRAPH', 'HEADING', 'BULLET_LISTS'],
				},
				content_ari: contentAri || undefined,
				locale,
			},
		},
		{
			apiPath: 'v2/ai-feature/editor-ai/stream',
			delayChunkUpdate: { gap: 10, time: 20 },
			enableFollowUp: false,
			enablePartialJson: false,
			skip,
			headers: {
				'x-experience-id': 'editor',
				'x-product': 'confluence',
			},
		},
	];
};

export const useTitleGenerationStreaming = ({
	contentId,
	cloudId,
	documentContext,
	locale,
	skip,
	onTitleChange,
}: UseTitleGenerationStreamingProps) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { trackAIResultView, trackAIResultError } = useAIEventsInstrumentation();
	const { pageAri } = useConfluencePageAri(cloudId, contentId, 'page');

	const skipRef = useRef(skip);
	skipRef.current = skip;

	const [request, config] = useMemo(
		() =>
			buildTitleGenerationRequest({
				contentAri: pageAri || '',
				documentContext,
				locale,
				skip: skipRef.current,
			}),
		[pageAri, documentContext, locale],
	);

	const onStateChange = useCallback(
		(
			state: ConvoAIStreamingState<
				EditorAiFinalResponseMessage,
				EditorAiErrorResponseMessage,
				never,
				EditorAiAnswerPartResponseMessage
			>,
		) => {
			switch (state.responseState) {
				case ConvoAIResponseState.AnswerPart:
					break;
				case ConvoAIResponseState.FinalResponse:
					onTitleChange?.(state.content);
					trackAIResultView({
						attributes: {
							aiFeatureName: 'aiTitleGenOnPaste',
							doesNotMeetMAUCriteria: true,
						},
					});
					experienceTracker?.succeed({
						name: AI_TITLE_GENERATION,
					});
					break;
				case ConvoAIResponseState.Error:
					const errorMessage = state.error?.message?.content || 'Title generation failed';
					if (!errorMessage.includes('missing valid content')) {
						trackAIResultError(
							{
								aiErrorMessage: errorMessage,
							},
							{
								attributes: {
									aiFeatureName: 'aiTitleGenOnPaste',
								},
							},
						);
						experienceTracker?.fail({
							name: AI_TITLE_GENERATION,
							error: new Error(errorMessage),
						});
					}
					break;
			}
		},
		[onTitleChange, experienceTracker, trackAIResultView, trackAIResultError],
	);

	const [{ content, responseState, error }] = useConvoAIStreaming<
		AiFeatureRequestEnvelopeEditorAiFeatureRequest,
		EditorAiFinalResponseMessage,
		EditorAiErrorResponseMessage,
		never,
		EditorAiAnswerPartResponseMessage
	>(request, config, onStateChange);

	return {
		content,
		responseState,
		error,
	};
};
